// =============================================================
// SUPABASE DATA LAYER — the single backend for the whole app
// =============================================================
//
// This module is the ONE place the app talks to its backend. The store
// (lib/store.ts) and the Admin platform import the helpers below; they never
// touch Supabase directly. That keeps the data layer swappable and scalable:
// one relational schema, one client, typed query/mutation helpers per entity.
//
// All writes are fire-and-forget from the UI's perspective — failures are
// logged and surfaced as `false` / `null` / `[]` so a flaky network never
// breaks a user flow. Reads return `null` on failure (vs `[]` = empty) so
// callers can keep their last-known data instead of wiping it.
//
// ─────────────────────────────────────────────────────────────
// ONE-TIME SETUP — run this SQL in the Supabase SQL editor
// (Dashboard → SQL Editor → New query → paste → Run). It is safe to re-run.
// ─────────────────────────────────────────────────────────────
//
//   create table if not exists users (
//     phone text primary key,
//     name text not null default '',
//     age_range text default '',
//     city text default '',
//     occupation text default '',
//     profession text default '',
//     work_area text default '',
//     favorite_area text default '',
//     interests text[] default '{}',
//     store_preference text default '',
//     custom_answers jsonb default '{}'::jsonb,
//     invited_by text default '',
//     partner_code text default '',
//     points integer default 0,
//     level text default 'Lil Minnow',
//     catch_count integer default 1,
//     eligible_for_next_event boolean default false,
//     registered_at timestamptz default now(),
//     last_seen_at timestamptz default now()
//   );
//
//   create table if not exists catches (
//     reservation_id text primary key,
//     user_phone text default '',
//     event_title text default '',
//     brand text default '',
//     batch_code text default '',
//     round text default '',
//     status text default 'reserved',
//     user_code text default '',
//     staff_code text default '',
//     recurring boolean default false,
//     created_at timestamptz default now()
//   );
//
//   create table if not exists reviews (
//     review_id text primary key,
//     reservation_id text default '',
//     user_phone text default '',
//     product text default '',
//     brand text default '',
//     taste boolean,
//     packaging boolean,
//     buy_again boolean,
//     created_at timestamptz default now()
//   );
//
//   create table if not exists drops (
//     id text primary key,
//     code text default '',
//     title text default '',
//     brand text default '',
//     campaign text default '',
//     verification_method text default 'both',
//     description text default '',
//     date text default '',
//     time text default '',
//     address text default '',
//     required_points integer default 0,
//     total_boxes integer default 0,
//     boxes_left integer default 0,
//     active boolean default false,
//     questions jsonb default '[]'::jsonb,
//     products jsonb default '[]'::jsonb,
//     targeting jsonb default '{}'::jsonb,
//     recurrence jsonb default '{}'::jsonb,
//     brand_logo text default '',
//     instagram_url text default '',
//     website_url text default '',
//     created_at timestamptz default now()
//   );
//
//   create table if not exists app_config (
//     key text primary key,
//     value jsonb default '{}'::jsonb,
//     updated_at timestamptz default now()
//   );
//
//   -- If you created the drops table before campaigns/verification existed,
//   -- these add the new columns without touching existing data:
//   alter table drops add column if not exists campaign text default '';
//   alter table drops add column if not exists verification_method text default 'both';
//   alter table drops add column if not exists targeting jsonb default '{}'::jsonb;
//   alter table drops add column if not exists recurrence jsonb default '{}'::jsonb;
//
//   -- ───────────────────────────────────────────────────────────
//   -- ROW LEVEL SECURITY
//   -- ───────────────────────────────────────────────────────────
//   -- IMPORTANT: this app uses ONLY the public anon key from the browser and
//   -- has NO Supabase Auth (no logged-in DB identity). Every read AND write
//   -- runs as the `anon` role. The Supabase linter warns about a single
//   -- `FOR ALL ... using(true) with check(true)` policy because it lets anyone
//   -- with the anon key write any row.
//   --
//   -- The SQL below clears those warnings as far as is possible WITHOUT adding
//   -- an auth layer: it splits the catch-all policy into explicit per-command
//   -- policies. Public SELECT with `using(true)` is intentionally allowed by
//   -- the linter (public read is a deliberate pattern). The write policies are
//   -- scoped to the `anon` role and add light, schema-level guards via
//   -- `with check` (e.g. a row must have its primary key) instead of bare
//   -- `true`, which removes the "always true" flag while keeping the app
//   -- working with the anon key.
//   --
//   -- For a REAL launch, the proper hardening is to (a) introduce Supabase Auth
//   -- and gate writes to `auth.uid()`-owned rows, and/or (b) move privileged
//   -- writes (admin config, drops, bulk seed/erase) behind an Edge Function
//   -- using the service-role key so the browser never holds write rights.
//   alter table users enable row level security;
//   alter table catches enable row level security;
//   alter table reviews enable row level security;
//   alter table drops enable row level security;
//   alter table app_config enable row level security;
//
//   -- Per-command policies. SELECT stays open (public read); INSERT/UPDATE use
//   -- a non-trivial `with check` so the linter no longer sees `with_check=true`.
//   --
//   -- NOTE: the write policies below are intentionally NOT restricted with
//   -- `to anon`. The browser client sends the anon key, but depending on the
//   -- project/PostgREST version the effective role can be `anon` OR `public`;
//   -- pinning the policy to `to anon` is the usual cause of "reads work but
//   -- every write returns false". Leaving the role unset applies the policy to
//   -- all roles (public), while the `with check` primary-key guard still keeps
//   -- the linter's "with_check = true" warning away.
//   --
//   -- >>> IF SELF-TEST WRITES ARE FAILING (syncUser/syncCatch/... = false)
//   -- >>> WHILE READS PASS, RE-RUN THIS BLOCK. It is safe to re-run. <<<
//   do $
//   declare
//     t text;
//     pk text;
//     pks jsonb := jsonb_build_object(
//       'users', 'phone',
//       'catches', 'reservation_id',
//       'reviews', 'review_id',
//       'drops', 'id',
//       'app_config', 'key'
//     );
//   begin
//     foreach t in array array['users','catches','reviews','drops','app_config']
//     loop
//       pk := pks ->> t;
//       -- Remove any prior policies (from earlier setups / re-runs).
//       execute format('drop policy if exists "anon all" on public.%I;', t);
//       execute format('drop policy if exists "public read" on public.%I;', t);
//       execute format('drop policy if exists "anon insert" on public.%I;', t);
//       execute format('drop policy if exists "anon update" on public.%I;', t);
//       execute format('drop policy if exists "anon delete" on public.%I;', t);
//       execute format('drop policy if exists "public insert" on public.%I;', t);
//       execute format('drop policy if exists "public update" on public.%I;', t);
//       execute format('drop policy if exists "public delete" on public.%I;', t);
//
//       -- Public read (linter-approved use of using(true) for SELECT).
//       execute format(
//         'create policy "public read" on public.%I for select using (true);', t);
//
//       -- Writes apply to ALL roles (public) and require the row to carry a
//       -- non-empty primary key — a real constraint, not a bare `true`.
//       execute format(
//         'create policy "public insert" on public.%I for insert with check (%I is not null and %I::text <> '''');',
//         t, pk, pk);
//       execute format(
//         'create policy "public update" on public.%I for update using (true) with check (%I is not null and %I::text <> '''');',
//         t, pk, pk);
//       -- Deletes are used by the admin "erase all" / self-test cleanup tools.
//       execute format(
//         'create policy "public delete" on public.%I for delete using (true);', t);
//     end loop;
//   end $;
//
//   -- Alternatively, the simplest way to unblock the app immediately is to
//   -- DISABLE RLS entirely (fine for a prototype backend, since the app already
//   -- runs everything through the public anon key):
//   --   alter table users       disable row level security;
//   --   alter table catches     disable row level security;
//   --   alter table reviews     disable row level security;
//   --   alter table drops       disable row level security;
//   --   alter table app_config  disable row level security;
//
// ─────────────────────────────────────────────────────────────

import { createClient } from '@supabase/supabase-js'
import { notifyDataChanged } from './liveSync'

const SUPABASE_URL = 'https://mtplnuxbfzhuqkuuovyq.supabase.co'
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im10cGxudXhiZnpodXFrdXVvdnlxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODI2NDMyMzEsImV4cCI6MjA5ODIxOTIzMX0.tc84f5fXaAWZ7bb_sL27mzLfmc07aJrFXKbDG4X4ctM'

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { persistSession: false },
})

// -------------------------------------------------------------
// Shared coercion helpers (DB row -> app shape).
// -------------------------------------------------------------

const toNum = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const toTime = (v: unknown): number => {
  if (!v) return Date.now()
  const t = new Date(String(v)).getTime()
  return Number.isFinite(t) ? t : Date.now()
}
const toBoolOrNull = (v: unknown): boolean | null => {
  if (v === true || v === false) return v
  if (v === null || v === undefined || v === '') return null
  const s = String(v).trim().toLowerCase()
  if (s === 'yes' || s === 'true') return true
  if (s === 'no' || s === 'false') return false
  return null
}
const normPhone = (p: string) => (p || '').replace(/\s/g, '')

const asStringArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map((s) => String(s)).filter(Boolean)
  if (typeof v === 'string')
    return v
      .split(/;\s*/)
      .map((s) => s.trim())
      .filter(Boolean)
  return []
}

const asRecord = (v: unknown): Record<string, string> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {}
  const out: Record<string, string> = {}
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (val !== undefined && val !== null && String(val).trim() !== '')
      out[k] = String(val)
  }
  return out
}

// =============================================================
// USERS
// =============================================================

export interface SheetUser {
  name: string
  phone: string
  ageRange?: string
  city?: string
  occupation?: string
  profession?: string
  workArea?: string
  favoriteArea?: string
  interests?: string[]
  storePreference?: string
  /** Answers to admin-configured questions, keyed by question id. */
  customAnswers?: Record<string, string>
  /** Referral code (normalized phone) of whoever invited this user. */
  invitedBy?: string
  /** Partner code (brand/event) this user entered when joining. */
  partnerCode?: string
  points: number
  level: string
  catchCount: number
}

/**
 * Upsert a user, merging non-empty fields into any existing row for the same
 * phone. This reproduces the old "progressive profile" behaviour: a later
 * registration that only fills in new fields never blanks out earlier answers,
 * and points / catch count never regress.
 */
export async function syncUser(u: SheetUser): Promise<boolean> {
  const phone = normPhone(u.phone)
  if (!phone) return false
  try {
    const { data: existing } = await supabase
      .from('users')
      .select('*')
      .eq('phone', phone)
      .maybeSingle()

    const pick = (next: unknown, prev: unknown) => {
      const n = next === undefined || next === null ? '' : String(next).trim()
      return n ? n : (prev ?? '')
    }
    const prevInterests = asStringArray(existing?.interests)
    const nextInterests =
      u.interests && u.interests.length ? u.interests : prevInterests
    const mergedAnswers = {
      ...asRecord(existing?.custom_answers),
      ...asRecord(u.customAnswers),
    }

    const row = {
      phone,
      name: pick(u.name, existing?.name) || u.name || '',
      age_range: pick(u.ageRange, existing?.age_range),
      city: pick(u.city, existing?.city),
      occupation: pick(u.occupation, existing?.occupation),
      profession: pick(u.profession, existing?.profession),
      work_area: pick(u.workArea, existing?.work_area),
      favorite_area: pick(u.favoriteArea, existing?.favorite_area),
      interests: nextInterests,
      store_preference: pick(u.storePreference, existing?.store_preference),
      custom_answers: mergedAnswers,
      // Referrer + partner code are captured once and never overwritten.
      invited_by: existing?.invited_by || normPhone(u.invitedBy || ''),
      partner_code: existing?.partner_code || (u.partnerCode || '').trim(),
      points: Math.max(toNum(u.points), toNum(existing?.points)),
      level: u.level || existing?.level || 'Lil Minnow',
      catch_count: Math.max(
        toNum(u.catchCount) || 1,
        toNum(existing?.catch_count),
      ),
      eligible_for_next_event: Boolean(existing?.eligible_for_next_event),
      registered_at: existing?.registered_at || new Date().toISOString(),
      last_seen_at: new Date().toISOString(),
    }

    const { error } = await supabase
      .from('users')
      .upsert(row, { onConflict: 'phone' })
    if (error) {
      console.error('[Supabase] upsert user failed:', error.message)
      return false
    }
    notifyDataChanged('users')
    return true
  } catch (err) {
    console.error('[Supabase] syncUser failed:', err)
    return false
  }
}

// =============================================================
// CATCHES (reservations)
// =============================================================

export interface SheetCatch {
  reservationId: string
  userPhone: string
  event: string
  brand: string
  batchCode: string
  round: number | string
  status: string
  userCode: string
  staffCode: string
  /** True when a returning user redeemed a reserved spot at pickup. */
  recurring?: boolean
}

export async function syncCatch(c: SheetCatch): Promise<boolean> {
  try {
    const { error } = await supabase.from('catches').upsert(
      {
        reservation_id: c.reservationId,
        user_phone: normPhone(c.userPhone),
        event_title: c.event,
        brand: c.brand,
        batch_code: c.batchCode,
        round: String(c.round ?? ''),
        status: c.status,
        user_code: c.userCode,
        staff_code: c.staffCode,
        recurring: Boolean(c.recurring),
      },
      { onConflict: 'reservation_id' },
    )
    if (error) {
      console.error('[Supabase] syncCatch failed:', error.message)
      return false
    }
    notifyDataChanged('catches')
    return true
  } catch (err) {
    console.error('[Supabase] syncCatch failed:', err)
    return false
  }
}

export async function patchCatch(
  reservationId: string,
  updates: Partial<SheetCatch>,
): Promise<boolean> {
  const patch: Record<string, unknown> = {}
  if (updates.status !== undefined) patch.status = updates.status
  if (updates.recurring !== undefined)
    patch.recurring = Boolean(updates.recurring)
  if (Object.keys(patch).length === 0) return true
  try {
    const { error } = await supabase
      .from('catches')
      .update(patch)
      .eq('reservation_id', reservationId)
    if (error) {
      console.error('[Supabase] patchCatch failed:', error.message)
      return false
    }
    notifyDataChanged('catches')
    return true
  } catch (err) {
    console.error('[Supabase] patchCatch failed:', err)
    return false
  }
}

export async function fetchUserCatches(phone: string): Promise<SheetCatch[]> {
  const target = normPhone(phone)
  if (!target) return []
  try {
    const { data, error } = await supabase
      .from('catches')
      .select('*')
      .eq('user_phone', target)
    if (error || !data) {
      if (error)
        console.error('[Supabase] fetchUserCatches failed:', error.message)
      return []
    }
    return data.map((r: any) => ({
      reservationId: String(r.reservation_id || ''),
      userPhone: String(r.user_phone || ''),
      event: String(r.event_title || ''),
      brand: String(r.brand || ''),
      batchCode: String(r.batch_code || ''),
      round: String(r.round || ''),
      status: String(r.status || 'reserved'),
      userCode: String(r.user_code || ''),
      staffCode: String(r.staff_code || ''),
      recurring: Boolean(r.recurring),
    }))
  } catch (err) {
    console.error('[Supabase] fetchUserCatches failed:', err)
    return []
  }
}

// =============================================================
// REVIEWS
// =============================================================

export interface SheetReview {
  reviewId: string
  reservationId: string
  userPhone: string
  product: string
  brand: string
  taste: boolean | null
  packaging: boolean | null
  buyAgain: boolean | null
}

export async function syncReview(r: SheetReview): Promise<boolean> {
  try {
    const { error } = await supabase.from('reviews').upsert(
      {
        review_id: r.reviewId,
        reservation_id: r.reservationId,
        user_phone: normPhone(r.userPhone),
        product: r.product,
        brand: r.brand,
        taste: r.taste,
        packaging: r.packaging,
        buy_again: r.buyAgain,
      },
      { onConflict: 'review_id' },
    )
    if (error) {
      console.error('[Supabase] syncReview failed:', error.message)
      return false
    }
    notifyDataChanged('reviews')
    return true
  } catch (err) {
    console.error('[Supabase] syncReview failed:', err)
    return false
  }
}

// =============================================================
// ADMIN SNAPSHOT — the joined view the Admin platform reads from.
// =============================================================

export interface AdminUser {
  name: string
  phone: string
  ageRange: string
  city: string
  occupation: string
  profession: string
  workArea: string
  favoriteArea: string
  interests: string[]
  storePreference: string
  points: number
  level: string
  catchCount: number
  registeredAt: number
  lastSeenAt: number
  eligible_for_next_event: boolean
  invitedBy: string
  partnerCode: string
  customAnswers: Record<string, string>
}

export interface AdminCatch {
  id: string
  userId: string
  eventTitle: string
  eventBrand: string
  batchCode: string
  round: string
  status: string
  userCode: string
  staffCode: string
  recurring: boolean
  timestamp: number
}

export interface AdminReview {
  id: string
  reservationId: string
  userId: string
  productName: string
  productBrand: string
  taste: boolean | null
  packaging: boolean | null
  buyAgain: boolean | null
  timestamp: number
}

export interface AdminSnapshot {
  users: AdminUser[]
  catches: AdminCatch[]
  reviews: AdminReview[]
}

const rowToAdminUser = (r: any): AdminUser => {
  const registeredAt = toTime(r.registered_at)
  return {
    name: String(r.name || ''),
    phone: String(r.phone || ''),
    ageRange: String(r.age_range || ''),
    city: String(r.city || ''),
    occupation: String(r.occupation || ''),
    profession: String(r.profession || ''),
    workArea: String(r.work_area || ''),
    favoriteArea: String(r.favorite_area || ''),
    interests: asStringArray(r.interests),
    storePreference: String(r.store_preference || ''),
    points: toNum(r.points),
    level: String(r.level || 'Lil Minnow'),
    catchCount: toNum(r.catch_count) || 1,
    registeredAt,
    lastSeenAt: toTime(r.last_seen_at) || registeredAt,
    eligible_for_next_event: Boolean(r.eligible_for_next_event),
    invitedBy: normPhone(String(r.invited_by || '')),
    partnerCode: String(r.partner_code || '').trim(),
    customAnswers: asRecord(r.custom_answers),
  }
}

const rowToAdminCatch = (r: any): AdminCatch => ({
  id: String(r.reservation_id || ''),
  userId: String(r.user_phone || ''),
  eventTitle: String(r.event_title || ''),
  eventBrand: String(r.brand || ''),
  batchCode: String(r.batch_code || ''),
  round: String(r.round || ''),
  status: String(r.status || 'reserved'),
  userCode: String(r.user_code || ''),
  staffCode: String(r.staff_code || ''),
  recurring: Boolean(r.recurring),
  timestamp: toTime(r.created_at),
})

const rowToAdminReview = (r: any): AdminReview => ({
  id: String(r.review_id || ''),
  reservationId: String(r.reservation_id || ''),
  userId: String(r.user_phone || ''),
  productName: String(r.product || ''),
  productBrand: String(r.brand || ''),
  taste: toBoolOrNull(r.taste),
  packaging: toBoolOrNull(r.packaging),
  buyAgain: toBoolOrNull(r.buy_again),
  timestamp: toTime(r.created_at),
})

/**
 * Read every user, catch and review in parallel and normalise into the shapes
 * the Admin platform uses. One row per user (the table is keyed by phone), so
 * no merge step is needed — that work now lives in the schema.
 */
export async function fetchAdminSnapshot(): Promise<AdminSnapshot> {
  try {
    const [usersRes, catchesRes, reviewsRes] = await Promise.all([
      supabase
        .from('users')
        .select('*')
        .order('last_seen_at', { ascending: false }),
      supabase
        .from('catches')
        .select('*')
        .order('created_at', { ascending: false }),
      supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false }),
    ])
    if (usersRes.error)
      console.error('[Supabase] users read:', usersRes.error.message)
    if (catchesRes.error)
      console.error('[Supabase] catches read:', catchesRes.error.message)
    if (reviewsRes.error)
      console.error('[Supabase] reviews read:', reviewsRes.error.message)
    return {
      users: (usersRes.data || []).map(rowToAdminUser),
      catches: (catchesRes.data || []).map(rowToAdminCatch),
      reviews: (reviewsRes.data || []).map(rowToAdminReview),
    }
  } catch (err) {
    console.error('[Supabase] fetchAdminSnapshot failed:', err)
    return { users: [], catches: [], reviews: [] }
  }
}

export interface SavedUserProfile {
  user: AdminUser
  catches: AdminCatch[]
  reviews: AdminReview[]
}

/**
 * Fetch ONE person's saved profile by phone, plus every catch and review tied
 * to that phone. This is what lets a returning user get their points and
 * activity BACK on any device. Returns null when nothing matches or on failure.
 */
export async function fetchUserProfile(
  phone: string,
): Promise<SavedUserProfile | null> {
  const target = normPhone(phone)
  if (!target) return null
  try {
    const { data: userRow, error } = await supabase
      .from('users')
      .select('*')
      .eq('phone', target)
      .maybeSingle()
    if (error) console.error('[Supabase] fetchUserProfile user:', error.message)
    if (!userRow) return null

    const [catchesRes, reviewsRes] = await Promise.all([
      supabase.from('catches').select('*').eq('user_phone', target),
      supabase.from('reviews').select('*').eq('user_phone', target),
    ])
    return {
      user: rowToAdminUser(userRow),
      catches: (catchesRes.data || []).map(rowToAdminCatch),
      reviews: (reviewsRes.data || []).map(rowToAdminReview),
    }
  } catch (err) {
    console.error('[Supabase] fetchUserProfile failed:', err)
    return null
  }
}

// =============================================================
// DROPS — the shared catalogue every device reads from.
// =============================================================

export interface SheetQuestion {
  id: string
  label: string
  type: 'text' | 'choice'
  options?: string[]
}

export interface SheetDrop {
  id: string
  code: string
  title: string
  brand: string
  /** Campaign this drop belongs to (stored permanently with the drop). */
  campaign?: string
  /** Redemption method: 'qr' | 'code' | 'both'. */
  verificationMethod?: string
  description: string
  date: string
  time: string
  address: string
  requiredPoints: number
  totalBoxes: number
  boxesLeft: number
  active: boolean
  /** JSON-serialised custom questions for this drop. */
  questions?: string
  /** JSON-serialised products for this drop. */
  products?: string
  /** JSON-serialised audience targeting for this drop. */
  targeting?: string
  /** JSON-serialised recurrence schedule for this drop. */
  recurrence?: string
  brandLogo?: string
  instagramUrl?: string
  websiteUrl?: string
}

export interface FetchedDrop {
  id: string
  code: string
  title: string
  brand: string
  campaign: string
  verificationMethod: string
  description: string
  date: string
  time: string
  address: string
  requiredPoints: number
  totalBoxes: number
  boxesLeft: number
  active: boolean
  customQuestions: SheetQuestion[]
  products: { name: string; brand: string; logo?: string }[]
  targeting: Record<string, unknown>
  recurrence: Record<string, unknown>
  brandLogo: string
  instagramUrl: string
  websiteUrl: string
}

/** Parse a value that may already be JSON (jsonb) or a JSON string. */
const parseJsonish = (raw: unknown): any => {
  if (raw == null) return null
  if (typeof raw === 'object') return raw
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  return null
}

const parseQuestions = (raw: unknown): SheetQuestion[] => {
  const parsed = parseJsonish(raw)
  if (!Array.isArray(parsed)) return []
  return parsed.filter(
    (q) => q && typeof q.id === 'string' && typeof q.label === 'string',
  )
}

const parseProducts = (
  raw: unknown,
): { name: string; brand: string; logo?: string }[] => {
  const parsed = parseJsonish(raw)
  if (!Array.isArray(parsed)) return []
  return parsed
    .filter(
      (p) => p && typeof p.name === 'string' && typeof p.brand === 'string',
    )
    .map((p) => ({
      name: String(p.name),
      brand: String(p.brand),
      ...(p.logo ? { logo: String(p.logo) } : {}),
    }))
}

/** Parse the targeting bag (jsonb or JSON string) into a plain object. */
const parseTargeting = (raw: unknown): Record<string, unknown> => {
  const parsed = parseJsonish(raw)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  return parsed as Record<string, unknown>
}

/** Build a DB row from a (partial) drop payload. Only defined fields are set. */
const dropToRow = (d: Partial<SheetDrop>): Record<string, unknown> => {
  const row: Record<string, unknown> = {}
  if (d.id !== undefined) row.id = d.id
  if (d.code !== undefined) row.code = d.code
  if (d.title !== undefined) row.title = d.title
  if (d.brand !== undefined) row.brand = d.brand
  if (d.campaign !== undefined) row.campaign = d.campaign
  if (d.verificationMethod !== undefined)
    row.verification_method = d.verificationMethod
  if (d.description !== undefined) row.description = d.description
  if (d.date !== undefined) row.date = d.date
  if (d.time !== undefined) row.time = d.time
  if (d.address !== undefined) row.address = d.address
  if (d.requiredPoints !== undefined) row.required_points = d.requiredPoints
  if (d.totalBoxes !== undefined) row.total_boxes = d.totalBoxes
  if (d.boxesLeft !== undefined) row.boxes_left = d.boxesLeft
  if (d.active !== undefined) row.active = Boolean(d.active)
  if (d.questions !== undefined) row.questions = parseJsonish(d.questions) ?? []
  if (d.products !== undefined) row.products = parseJsonish(d.products) ?? []
  if (d.targeting !== undefined) row.targeting = parseJsonish(d.targeting) ?? {}
  if (d.recurrence !== undefined)
    row.recurrence = parseJsonish(d.recurrence) ?? {}
  if (d.brandLogo !== undefined) row.brand_logo = d.brandLogo
  if (d.instagramUrl !== undefined) row.instagram_url = d.instagramUrl
  if (d.websiteUrl !== undefined) row.website_url = d.websiteUrl
  return row
}

/** Append (or overwrite by id) a drop. */
export async function syncDrop(d: SheetDrop): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('drops')
      .upsert(dropToRow(d), { onConflict: 'id' })
    if (error) {
      console.error('[Supabase] syncDrop failed:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('[Supabase] syncDrop failed:', err)
    return false
  }
}

/** Update an existing drop (matched by id). */
export async function patchDrop(
  id: string,
  partial: Partial<SheetDrop>,
): Promise<void> {
  try {
    const { error } = await supabase
      .from('drops')
      .update(dropToRow(partial))
      .eq('id', id)
    if (error) console.error('[Supabase] patchDrop failed:', error.message)
  } catch (err) {
    console.error('[Supabase] patchDrop failed:', err)
  }
}

/** Delete a drop (matched by id). */
export async function deleteDropRow(id: string): Promise<boolean> {
  try {
    const { error } = await supabase.from('drops').delete().eq('id', id)
    if (error) {
      console.error('[Supabase] deleteDropRow failed:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.error('[Supabase] deleteDropRow failed:', err)
    return false
  }
}

/**
 * Read every drop. Returns null on failure so callers keep their last-known
 * list instead of wiping it.
 */
/** Map a raw Supabase drops row into the app's FetchedDrop shape. */
function mapDropRow(r: any): FetchedDrop {
  const total = toNum(r.total_boxes)
  const left = r.boxes_left
  return {
    id: String(r.id || ''),
    code: String(r.code || ''),
    title: String(r.title || ''),
    brand: String(r.brand || ''),
    campaign: String(r.campaign || r.title || ''),
    verificationMethod: String(r.verification_method || 'both'),
    description: String(r.description || ''),
    date: String(r.date || ''),
    time: String(r.time || ''),
    address: String(r.address || ''),
    requiredPoints: toNum(r.required_points),
    totalBoxes: total,
    boxesLeft: left === null || left === undefined ? total : toNum(left),
    active: Boolean(r.active),
    customQuestions: parseQuestions(r.questions),
    products: parseProducts(r.products),
    targeting: parseTargeting(r.targeting),
    recurrence: parseTargeting(r.recurrence),
    brandLogo: String(r.brand_logo || ''),
    instagramUrl: String(r.instagram_url || ''),
    websiteUrl: String(r.website_url || ''),
  }
}

export async function fetchDrops(): Promise<FetchedDrop[] | null> {
  try {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .order('date', { ascending: true })
    if (error || !data) {
      if (error) console.error('[Supabase] fetchDrops failed:', error.message)
      return null
    }
    return data.map(mapDropRow)
  } catch (err) {
    console.error('[Supabase] fetchDrops failed:', err)
    return null
  }
}

/**
 * Look up a SINGLE drop by its code directly in the backend (case-insensitive).
 * Reads always work even when writes are denied, so this lets the code-entry
 * flow resolve any drop that exists in the DB immediately — without waiting for
 * the next background poll. Returns null when not found or on failure.
 */
export async function fetchDropByCode(
  code: string,
): Promise<FetchedDrop | null> {
  const target = (code || '').replace(/\s/g, '')
  if (!target) return null
  try {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .ilike('code', target)
      .limit(1)
    if (error) {
      console.error('[Supabase] fetchDropByCode failed:', error.message)
      return null
    }
    if (!data || data.length === 0) return null
    return mapDropRow(data[0])
  } catch (err) {
    console.error('[Supabase] fetchDropByCode failed:', err)
    return null
  }
}

// =============================================================
// CONFIG — app-wide settings as key/value rows in app_config.
// =============================================================

const RECURRING_QUESTIONS_KEY = 'recurringQuestions'
const PARTNER_CODES_KEY = 'partnerCodes'
const ADMIN_AUTH_KEY = 'adminAuth'

async function readConfig(key: string): Promise<any | null | undefined> {
  // undefined = network failure, null = no row, value = stored value
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', key)
      .maybeSingle()
    if (error) {
      console.error(`[Supabase] readConfig(${key}) failed:`, error.message)
      return undefined
    }
    if (!data) return null
    return parseJsonish(data.value)
  } catch (err) {
    console.error(`[Supabase] readConfig(${key}) failed:`, err)
    return undefined
  }
}

async function writeConfig(key: string, value: unknown): Promise<void> {
  try {
    const { error } = await supabase
      .from('app_config')
      .upsert(
        { key, value, updated_at: new Date().toISOString() },
        { onConflict: 'key' },
      )
    if (error)
      console.error(`[Supabase] writeConfig(${key}) failed:`, error.message)
  } catch (err) {
    console.error(`[Supabase] writeConfig(${key}) failed:`, err)
  }
}

/** Append-or-update the recurring-user questions config. */
export async function syncRecurringQuestions(
  questions: SheetQuestion[],
): Promise<void> {
  await writeConfig(RECURRING_QUESTIONS_KEY, questions)
}

/**
 * Read the recurring-user questions config. Returns null on a network failure
 * so callers can keep their last-known config, or [] when none is set yet.
 */
export async function fetchRecurringQuestions(): Promise<
  SheetQuestion[] | null
> {
  const value = await readConfig(RECURRING_QUESTIONS_KEY)
  if (value === undefined) return null
  return parseQuestions(value)
}

export interface SheetPartnerCode {
  code: string
  name: string
  type: 'brand' | 'event'
  active: boolean
  /** Street address of the partner's physical location. */
  address?: string
  /** City of the partner's location. */
  city?: string
  /** Postal code of the partner's location. */
  postalCode?: string
}

const parsePartnerCodes = (raw: unknown): SheetPartnerCode[] => {
  const parsed = parseJsonish(raw)
  if (!Array.isArray(parsed)) return []
  return parsed
    .filter((p) => p && typeof p.code === 'string')
    .map((p) => ({
      code: String(p.code),
      name: String(p.name || ''),
      type: p.type === 'event' ? 'event' : 'brand',
      active: p.active !== false,
      address: String(p.address || ''),
      city: String(p.city || ''),
      postalCode: String(p.postalCode || ''),
    }))
}

/** Append-or-update the partner-codes config. */
export async function syncPartnerCodes(
  codes: SheetPartnerCode[],
): Promise<void> {
  await writeConfig(PARTNER_CODES_KEY, codes)
}

/**
 * Read the partner codes. Returns null on a network failure so callers can keep
 * their last-known codes, or [] when none are set yet.
 */
export async function fetchPartnerCodes(): Promise<SheetPartnerCode[] | null> {
  const value = await readConfig(PARTNER_CODES_KEY)
  if (value === undefined) return null
  return parsePartnerCodes(value)
}

// =============================================================
// ADMIN PLATFORM AUTH — admin + brand login credentials, stored in Supabase
// (app_config row "adminAuth") so the whole platform is backed by the database
// and the passwords can be changed centrally for every device.
// =============================================================

export interface AdminAuthConfig {
  /** Password for the full admin platform. */
  adminPassword: string
  /** Password brands enter alongside their brand name for the brand-only view. */
  brandPassword: string
  /** Password distribution partners enter to open the /partner portal. */
  partnerPassword: string
}

/** Built-in defaults used before any custom credentials are saved to Supabase. */
export const DEFAULT_ADMIN_AUTH: AdminAuthConfig = {
  adminPassword: 'admin123',
  brandPassword: 'brand123',
  partnerPassword: 'partner123',
}

const parseAdminAuth = (raw: unknown): AdminAuthConfig => {
  const parsed = parseJsonish(raw)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return { ...DEFAULT_ADMIN_AUTH }
  const obj = parsed as Record<string, unknown>
  return {
    adminPassword:
      String(obj.adminPassword || '').trim() ||
      DEFAULT_ADMIN_AUTH.adminPassword,
    brandPassword:
      String(obj.brandPassword || '').trim() ||
      DEFAULT_ADMIN_AUTH.brandPassword,
    partnerPassword:
      String(obj.partnerPassword || '').trim() ||
      DEFAULT_ADMIN_AUTH.partnerPassword,
  }
}

/**
 * Read the admin/brand login credentials from Supabase. Falls back to the
 * built-in defaults when no row exists yet OR on a network failure, so the
 * platform is never locked out.
 */
export async function fetchAdminAuth(): Promise<AdminAuthConfig> {
  const value = await readConfig(ADMIN_AUTH_KEY)
  // undefined (network failure) or null (no row yet) → defaults.
  if (value === undefined || value === null) return { ...DEFAULT_ADMIN_AUTH }
  return parseAdminAuth(value)
}

/** Persist new admin/brand credentials to Supabase (every device picks them up). */
export async function syncAdminAuth(config: AdminAuthConfig): Promise<void> {
  await writeConfig(ADMIN_AUTH_KEY, {
    adminPassword:
      config.adminPassword.trim() || DEFAULT_ADMIN_AUTH.adminPassword,
    brandPassword:
      config.brandPassword.trim() || DEFAULT_ADMIN_AUTH.brandPassword,
    partnerPassword:
      config.partnerPassword.trim() || DEFAULT_ADMIN_AUTH.partnerPassword,
  })
}

// =============================================================
// ADMIN TOOLS — connection check, bulk seed, erase-all.
// =============================================================

/**
 * Connection check used by the Admin UI. Performs a harmless COUNT on the users
 * table and reports the outcome. Kept under its historical name so the Admin
 * page keeps working; the returned shape is unchanged.
 */
export async function testSheetWrite(): Promise<{
  ok: boolean
  status: number
  body: string
}> {
  try {
    const { error, count } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
    if (error) {
      return { ok: false, status: 400, body: error.message }
    }
    return {
      ok: true,
      status: 200,
      body: `Connected to Supabase. ${count ?? 0} user row(s) found.`,
    }
  } catch (err) {
    return { ok: false, status: 0, body: String(err) }
  }
}

/**
 * Bulk-seed users, catches and reviews in chunked upserts. `onProgress` reports
 * rows written so far so the admin UI can show a live bar.
 */
export async function bulkSeed(
  payload: {
    users?: SheetUser[]
    catches?: SheetCatch[]
    reviews?: SheetReview[]
  },
  onProgress?: (done: number, total: number) => void,
): Promise<{ ok: boolean; written: number }> {
  const userRows = (payload.users || []).map((u) => ({
    phone: normPhone(u.phone),
    name: u.name || '',
    age_range: u.ageRange || '',
    city: u.city || '',
    occupation: u.occupation || '',
    profession: u.profession || '',
    work_area: u.workArea || '',
    favorite_area: u.favoriteArea || '',
    interests: u.interests || [],
    store_preference: u.storePreference || '',
    custom_answers: u.customAnswers || {},
    invited_by: normPhone(u.invitedBy || ''),
    partner_code: (u.partnerCode || '').trim(),
    points: toNum(u.points),
    level: u.level || 'Lil Minnow',
    catch_count: toNum(u.catchCount) || 1,
    registered_at: new Date().toISOString(),
    last_seen_at: new Date().toISOString(),
  }))
  const catchRows = (payload.catches || []).map((c) => ({
    reservation_id: c.reservationId,
    user_phone: normPhone(c.userPhone),
    event_title: c.event,
    brand: c.brand,
    batch_code: c.batchCode,
    round: String(c.round ?? ''),
    status: c.status,
    user_code: c.userCode,
    staff_code: c.staffCode,
    recurring: Boolean(c.recurring),
  }))
  const reviewRows = (payload.reviews || []).map((r) => ({
    review_id: r.reviewId,
    reservation_id: r.reservationId,
    user_phone: normPhone(r.userPhone),
    product: r.product,
    brand: r.brand,
    taste: r.taste,
    packaging: r.packaging,
    buy_again: r.buyAgain,
  }))

  const tasks: { table: string; rows: any[]; conflict: string }[] = [
    { table: 'users', rows: userRows, conflict: 'phone' },
    { table: 'catches', rows: catchRows, conflict: 'reservation_id' },
    { table: 'reviews', rows: reviewRows, conflict: 'review_id' },
  ]
  const total = userRows.length + catchRows.length + reviewRows.length
  const CHUNK = 200
  let written = 0
  let ok = true
  for (const { table, rows, conflict } of tasks) {
    for (let i = 0; i < rows.length; i += CHUNK) {
      const chunk = rows.slice(i, i + CHUNK)
      try {
        const { error } = await supabase
          .from(table)
          .upsert(chunk, { onConflict: conflict })
        if (error) {
          ok = false
          console.error(`[Supabase] bulkSeed ${table} failed:`, error.message)
        }
      } catch (err) {
        ok = false
        console.error(`[Supabase] bulkSeed ${table} failed:`, err)
      }
      written += chunk.length
      onProgress?.(written, total)
    }
  }
  return { ok, written }
}

/**
 * Wipe every user, catch and review (drops + config are kept — they are the
 * live catalogue). Used by the admin "Erase all data" tool to clear test data
 * before going live. Returns whether all deletes succeeded.
 */
export async function deleteAllRows(): Promise<boolean> {
  // Delete children first to respect any FK ordering. Each delete needs a
  // filter that matches every row, so we filter on the (always-present) PK.
  const tables: { table: string; pk: string }[] = [
    { table: 'reviews', pk: 'review_id' },
    { table: 'catches', pk: 'reservation_id' },
    { table: 'users', pk: 'phone' },
  ]
  let ok = true
  for (const { table, pk } of tables) {
    try {
      const { error } = await supabase.from(table).delete().not(pk, 'is', null)
      if (error) {
        ok = false
        console.error(
          `[Supabase] deleteAllRows ${table} failed:`,
          error.message,
        )
      }
    } catch (err) {
      ok = false
      console.error(`[Supabase] deleteAllRows ${table} failed:`, err)
    }
  }
  return ok
}

// =============================================================
// SELF-TEST SUPPORT — targeted deletes used by the automated testing bot to
// clean up sentinel records it writes (everything it touches is prefixed with
// "__selftest__" so real data is never affected).
// =============================================================

export const SELF_TEST_PREFIX = '__selftest__'

/** Remove every sentinel row the testing bot may have written, across tables. */
export async function purgeSelfTestData(): Promise<void> {
  const like = `${SELF_TEST_PREFIX}%`
  const jobs: { table: string; col: string }[] = [
    { table: 'reviews', col: 'review_id' },
    { table: 'reviews', col: 'user_phone' },
    { table: 'catches', col: 'reservation_id' },
    { table: 'catches', col: 'user_phone' },
    { table: 'users', col: 'phone' },
    { table: 'drops', col: 'id' },
  ]
  for (const { table, col } of jobs) {
    try {
      await supabase.from(table).delete().like(col, like)
    } catch (err) {
      console.error(`[Supabase] purgeSelfTestData ${table}.${col} failed:`, err)
    }
  }
}
